import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import {
  ArrowDown, ArrowLeft, ArrowUp, ExternalLink, Link2, Loader, Pencil, Plus, RefreshCw, ShieldAlert, Trash2, X, Check,
} from 'lucide-react';
import { borrarLink, crearLink, editarLink, errorLinks, getLinks, ordenarLinks, verificarLink } from './linksPersonalizados.api';
import { MAX_NOMBRE, completarUrl, dominio, errorNombre, errorUrl } from './validarLink';

// Portal de empresa › "Link personalizados": la empresa guarda accesos a sus propias herramientas (tableros,
// planillas, mapas…) y los ve embebidos dentro de LisualPro. Los invitados sólo los ven.
// Muchos sitios prohíben verse dentro de otra página (X-Frame-Options / CSP): el backend lo revisa al guardar
// (embebible = false) y, si no pudo saberlo, acá avisamos si el sitio no termina de cargar.

const MAX_POR_DEFECTO = 20;
const ESPERA_CARGA_MS = 12000;
const SANDBOX = 'allow-scripts allow-same-origin allow-forms allow-popups';

const abrirAfuera = (url) => window.open(url, '_blank', 'noopener,noreferrer');

function Inicial({ nombre }) {
  return (
    <span className="flex-shrink-0 w-10 h-10 rounded-xl bg-blue-100 text-blue-700 font-semibold flex items-center justify-center uppercase" aria-hidden="true">
      {String(nombre || '?').trim().charAt(0) || '?'}
    </span>
  );
}

function BotonIcono({ titulo, onClick, disabled, children, peligro = false }) {
  return (
    <button
      type="button"
      title={titulo}
      aria-label={titulo}
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      disabled={disabled}
      className={`w-9 h-9 flex items-center justify-center rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 ${peligro ? 'text-red-500 hover:bg-red-50' : 'text-gray-500 hover:bg-gray-100 hover:text-blue-700'}`}
    >
      {children}
    </button>
  );
}

/** Formulario de nombre + URL (agregar y editar usan el mismo). */
function FormLink({ inicial, textoBoton, onGuardar, onCancelar, guardando, compacto = false }) {
  const [nombre, setNombre] = useState(inicial?.nombre || '');
  const [url, setUrl] = useState(inicial?.url || '');
  const [errores, setErrores] = useState({});
  const refNombre = useRef(null);

  useEffect(() => {
    if (compacto) refNombre.current?.focus();
  }, [compacto]);

  const enviar = async (e) => {
    e.preventDefault();
    const urlCompleta = completarUrl(url);
    const err = { nombre: errorNombre(nombre), url: errorUrl(urlCompleta) };
    setErrores(err);
    if (err.nombre || err.url) return;
    const ok = await onGuardar({ nombre: nombre.replace(/\s+/g, ' ').trim(), url: urlCompleta });
    if (ok && !inicial) {
      setNombre('');
      setUrl('');
    }
  };

  const campo = 'w-full min-w-0 rounded-lg border px-3 py-2.5 text-base sm:text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-400';
  return (
    <form onSubmit={enviar} noValidate className={`grid gap-3 ${compacto ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-[minmax(0,1fr)_minmax(0,2fr)_auto]'} items-start`}>
      <div className="min-w-0">
        <label className="block text-xs font-medium text-gray-600 mb-1" htmlFor={`${inicial?.id || 'nuevo'}-nombre`}>Nombre</label>
        <input
          ref={refNombre}
          id={`${inicial?.id || 'nuevo'}-nombre`}
          value={nombre}
          maxLength={MAX_NOMBRE + 10}
          onChange={(e) => setNombre(e.target.value)}
          placeholder="Ej: Tablero de avance"
          className={`${campo} ${errores.nombre ? 'border-red-400' : 'border-gray-300'}`}
          aria-invalid={!!errores.nombre}
          aria-describedby={errores.nombre ? `${inicial?.id || 'nuevo'}-nombre-err` : undefined}
        />
        {errores.nombre && <p id={`${inicial?.id || 'nuevo'}-nombre-err`} className="mt-1 text-xs text-red-600">{errores.nombre}</p>}
      </div>
      <div className="min-w-0">
        <label className="block text-xs font-medium text-gray-600 mb-1" htmlFor={`${inicial?.id || 'nuevo'}-url`}>Dirección (https://)</label>
        <input
          id={`${inicial?.id || 'nuevo'}-url`}
          value={url}
          inputMode="url"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          onChange={(e) => setUrl(e.target.value)}
          onBlur={() => url && setUrl(completarUrl(url))}
          placeholder="https://…"
          className={`${campo} ${errores.url ? 'border-red-400' : 'border-gray-300'}`}
          aria-invalid={!!errores.url}
          aria-describedby={errores.url ? `${inicial?.id || 'nuevo'}-url-err` : undefined}
        />
        {errores.url && <p id={`${inicial?.id || 'nuevo'}-url-err`} className="mt-1 text-xs text-red-600">{errores.url}</p>}
      </div>
      <div className={`flex gap-2 ${compacto ? '' : 'md:pt-5'}`}>
        <button
          type="submit"
          disabled={guardando}
          className="flex-1 md:flex-none inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60 whitespace-nowrap"
        >
          {guardando ? <Loader size={16} className="animate-spin" /> : inicial ? <Check size={16} /> : <Plus size={16} />}
          {guardando ? 'Guardando…' : textoBoton}
        </button>
        {onCancelar && (
          <button type="button" onClick={onCancelar} className="flex-1 md:flex-none rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50">
            Cancelar
          </button>
        )}
      </div>
    </form>
  );
}

/** El link abierto dentro del portal. */
function VistaEmbebida({ link, links, onVolver, onAbrir, onVerificar }) {
  const [cargado, setCargado] = useState(false);
  const [lento, setLento] = useState(false);
  const [forzar, setForzar] = useState(false);
  const [recarga, setRecarga] = useState(0);
  const bloqueado = link.embebible === false && !forzar;

  useEffect(() => {
    setCargado(false);
    setLento(false);
    if (bloqueado) return undefined;
    const t = setTimeout(() => setLento(true), ESPERA_CARGA_MS);
    return () => clearTimeout(t);
  }, [link.id, link.url, bloqueado, recarga]);

  // Si al guardar no se pudo saber si el sitio deja embeberse, lo pedimos de nuevo en segundo plano.
  useEffect(() => {
    if (link.embebible === null) onVerificar(link.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [link.id]);

  return (
    <section className="flex flex-col h-full min-h-0 bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" aria-label={link.nombre}>
      <header className="flex items-center gap-2 px-2 sm:px-3 py-2 border-b border-gray-200 bg-white">
        <BotonIcono titulo="Volver a los links" onClick={onVolver}><ArrowLeft size={20} /></BotonIcono>
        <div className="min-w-0 flex-1">
          {links.length > 1 ? (
            <select
              value={link.id}
              onChange={(e) => onAbrir(e.target.value)}
              className="max-w-full truncate bg-transparent font-semibold text-gray-800 text-sm sm:text-base pr-6 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 rounded"
              aria-label="Cambiar de link"
            >
              {links.map((l) => <option key={l.id} value={l.id}>{l.nombre}</option>)}
            </select>
          ) : (
            <h2 className="m-0 p-0 truncate font-semibold text-gray-800 text-sm sm:text-base">{link.nombre}</h2>
          )}
          <p className="truncate text-xs text-gray-500">{dominio(link.url)}</p>
        </div>
        {!bloqueado && (
          <BotonIcono titulo="Recargar" onClick={() => setRecarga((n) => n + 1)}><RefreshCw size={18} /></BotonIcono>
        )}
        <button
          type="button"
          onClick={() => abrirAfuera(link.url)}
          aria-label="Abrir en pestaña nueva"
          className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-700 whitespace-nowrap"
        >
          <ExternalLink size={16} />
          <span className="hidden sm:inline">Abrir en pestaña nueva</span>
          <span className="sm:hidden">Abrir</span>
        </button>
      </header>

      {bloqueado ? (
        <div className="flex-1 flex items-center justify-center p-6 bg-gray-50">
          <div className="max-w-md text-center">
            <ShieldAlert size={44} className="mx-auto text-amber-500" />
            <h3 className="mx-0 mb-0 p-0 mt-3 text-lg font-semibold text-gray-800">Este sitio no permite verse embebido</h3>
            <p className="mt-2 text-sm text-gray-600">
              {dominio(link.url)} bloquea que se lo muestre dentro de otra página. Lo podés abrir igual en una pestaña nueva.
            </p>
            <button type="button" onClick={() => abrirAfuera(link.url)} className="mt-5 inline-flex items-center gap-2 rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-blue-700">
              <ExternalLink size={16} /> Abrir en pestaña nueva
            </button>
            <div>
              <button type="button" onClick={() => setForzar(true)} className="mt-3 text-xs text-gray-500 underline hover:text-gray-700">
                Intentar mostrarlo igual
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="relative flex-1 min-h-[320px] bg-gray-50">
          {!cargado && !lento && (
            <div className="absolute inset-0 z-10 flex items-center justify-center text-gray-500 gap-2 text-sm pointer-events-none bg-gray-50">
              <Loader size={20} className="animate-spin" /> Cargando {dominio(link.url)}…
            </div>
          )}
          <iframe
            key={`${link.id}-${recarga}`}
            title={link.nombre}
            src={link.url}
            sandbox={SANDBOX}
            referrerPolicy="no-referrer"
            allow="fullscreen"
            allowFullScreen
            onLoad={() => setCargado(true)}
            className="absolute inset-0 w-full h-full border-0 bg-white"
          />
          {lento && !cargado && (
            <div className="absolute z-20 inset-x-3 bottom-3 sm:inset-x-auto sm:right-4 sm:bottom-4 sm:max-w-sm rounded-xl bg-white shadow-lg border border-amber-200 p-4" role="status">
              <p className="text-sm font-semibold text-gray-800">Este sitio no permite verse embebido o tarda en cargar</p>
              <p className="mt-1 text-xs text-gray-600">Si no ves el contenido, abrilo en una pestaña nueva.</p>
              <button type="button" onClick={() => abrirAfuera(link.url)} className="mt-3 inline-flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-700">
                <ExternalLink size={16} /> Abrir en pestaña nueva
              </button>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

export default function LinksPersonalizados({ customerId }) {
  const [estado, setEstado] = useState('cargando'); // cargando | listo | error
  const [errorCarga, setErrorCarga] = useState('');
  const [links, setLinks] = useState([]);
  const [puedeEditar, setPuedeEditar] = useState(false);
  const [max, setMax] = useState(MAX_POR_DEFECTO);
  const [abierto, setAbierto] = useState(null);
  const [editando, setEditando] = useState(null);
  const [aBorrar, setABorrar] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [ocupado, setOcupado] = useState(false);

  const aplicar = (r) => {
    setLinks(Array.isArray(r?.links) ? r.links : []);
    if (typeof r?.puedeEditar === 'boolean') setPuedeEditar(r.puedeEditar);
    if (r?.limites?.links) setMax(r.limites.links);
  };

  const cargar = useCallback(async () => {
    if (!customerId) return;
    setEstado('cargando');
    try {
      aplicar(await getLinks(customerId));
      setEstado('listo');
    } catch (e) {
      setErrorCarga(errorLinks(e));
      setEstado('error');
    }
  }, [customerId]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const agregar = async (datos) => {
    setGuardando(true);
    try {
      const r = await crearLink(customerId, datos);
      aplicar(r);
      const nuevo = r.links[r.links.length - 1];
      toast.success(nuevo?.embebible === false ? 'Link agregado. Ese sitio se abre en una pestaña nueva.' : 'Link agregado');
      return true;
    } catch (e) {
      toast.error(errorLinks(e));
      return false;
    } finally {
      setGuardando(false);
    }
  };

  const guardarEdicion = async (link, datos) => {
    const cambios = {};
    if (datos.nombre !== link.nombre) cambios.nombre = datos.nombre;
    if (datos.url !== link.url) cambios.url = datos.url;
    if (!Object.keys(cambios).length) {
      setEditando(null);
      return true;
    }
    setGuardando(true);
    try {
      aplicar(await editarLink(customerId, link.id, cambios));
      setEditando(null);
      toast.success('Cambios guardados');
      return true;
    } catch (e) {
      toast.error(errorLinks(e));
      return false;
    } finally {
      setGuardando(false);
    }
  };

  const borrar = async (link) => {
    setOcupado(true);
    try {
      aplicar(await borrarLink(customerId, link.id));
      setABorrar(null);
      if (abierto === link.id) setAbierto(null);
      toast.success('Link borrado');
    } catch (e) {
      toast.error(errorLinks(e));
    } finally {
      setOcupado(false);
    }
  };

  const mover = async (i, delta) => {
    const j = i + delta;
    if (j < 0 || j >= links.length) return;
    const previo = links;
    const nuevo = [...links];
    [nuevo[i], nuevo[j]] = [nuevo[j], nuevo[i]];
    setLinks(nuevo); // se ve al instante; si falla se vuelve atrás
    setOcupado(true);
    try {
      aplicar(await ordenarLinks(customerId, nuevo.map((l) => l.id)));
    } catch (e) {
      setLinks(previo);
      toast.error(errorLinks(e));
    } finally {
      setOcupado(false);
    }
  };

  const verificar = useCallback(async (linkId) => {
    try {
      const r = await verificarLink(customerId, linkId);
      if (r?.link) setLinks((ls) => ls.map((l) => (l.id === linkId ? r.link : l)));
    } catch (e) {
      // es sólo una ayuda: si falla, queda el aviso por demora de carga
    }
  }, [customerId]);

  const linkAbierto = links.find((l) => l.id === abierto);
  if (linkAbierto) {
    return (
      <div className="h-full min-h-[420px] flex flex-col">
        <VistaEmbebida link={linkAbierto} links={links} onVolver={() => setAbierto(null)} onAbrir={setAbierto} onVerificar={verificar} />
      </div>
    );
  }

  const lleno = links.length >= max;

  return (
    <div className="max-w-4xl mx-auto w-full">
      <div className="flex items-start gap-3 mb-4">
        <span className="flex-shrink-0 w-11 h-11 mt-0.5 rounded-xl bg-blue-600 text-white flex items-center justify-center"><Link2 size={22} /></span>
        <div className="min-w-0">
          <h1 className="m-0 p-0 text-xl sm:text-2xl font-bold text-gray-800 leading-tight">Link personalizados</h1>
          <p className="m-0 mt-1 text-sm text-gray-600">Tus accesos a otras herramientas (tableros, planillas, mapas) para verlos sin salir de LisualPro.</p>
        </div>
      </div>

      {puedeEditar && estado === 'listo' && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-3 sm:p-4 mb-4">
          <div className="flex items-center justify-between mb-3 gap-2">
            <h2 className="m-0 p-0 text-base font-semibold text-gray-800">Agregar link</h2>
            <span className={`text-xs ${lleno ? 'text-red-600 font-semibold' : 'text-gray-500'}`}>{links.length} de {max}</span>
          </div>
          {lleno ? (
            <p className="text-sm text-gray-600">Llegaste al máximo de {max} links. Borrá alguno para agregar otro.</p>
          ) : (
            <FormLink textoBoton="Agregar link" onGuardar={agregar} guardando={guardando} />
          )}
        </div>
      )}

      {estado === 'cargando' && (
        <ul className="space-y-2" aria-busy="true" aria-label="Cargando links">
          {[0, 1, 2].map((i) => (
            <li key={i} className="h-16 rounded-xl bg-white border border-gray-200 animate-pulse" />
          ))}
        </ul>
      )}

      {estado === 'error' && (
        <div className="bg-white rounded-xl border border-red-200 p-6 text-center" role="alert">
          <p className="font-semibold text-gray-800">No pudimos cargar los links</p>
          <p className="mt-1 text-sm text-gray-600">{errorCarga}</p>
          <button type="button" onClick={cargar} className="mt-4 inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
            <RefreshCw size={16} /> Reintentar
          </button>
        </div>
      )}

      {estado === 'listo' && links.length === 0 && (
        <div className="bg-white rounded-xl border border-dashed border-gray-300 p-8 text-center">
          <Link2 size={36} className="mx-auto text-gray-300" />
          <p className="mt-3 font-semibold text-gray-800">{puedeEditar ? 'Todavía no agregaste links' : 'Todavía no hay links personalizados'}</p>
          <p className="mt-1 text-sm text-gray-500">
            {puedeEditar ? 'Pegá arriba la dirección de un tablero, una planilla o cualquier sitio que uses seguido.' : 'Cuando tu empresa agregue links, los vas a ver acá.'}
          </p>
        </div>
      )}

      {estado === 'listo' && links.length > 0 && (
        <ul className="space-y-2">
          {links.map((l, i) => (
            <li key={l.id} className="bg-white rounded-xl border border-gray-200 shadow-sm">
              {editando === l.id ? (
                <div className="p-3 sm:p-4">
                  <FormLink inicial={l} compacto textoBoton="Guardar" guardando={guardando} onGuardar={(d) => guardarEdicion(l, d)} onCancelar={() => setEditando(null)} />
                </div>
              ) : aBorrar === l.id ? (
                <div className="flex flex-wrap items-center gap-2 p-3 sm:p-4">
                  <p className="flex-1 min-w-0 text-sm text-gray-800">¿Borrar <strong className="break-words">{l.nombre}</strong>?</p>
                  <button type="button" disabled={ocupado} onClick={() => borrar(l)} className="inline-flex items-center gap-1 rounded-lg bg-red-600 px-3 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-60">
                    <Trash2 size={16} /> Sí, borrar
                  </button>
                  <button type="button" onClick={() => setABorrar(null)} className="inline-flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">
                    <X size={16} /> Cancelar
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2 p-2 sm:p-3">
                  <button
                    type="button"
                    onClick={() => setAbierto(l.id)}
                    className="flex flex-1 min-w-0 items-center gap-3 text-left rounded-lg p-1 hover:bg-blue-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
                  >
                    <Inicial nombre={l.nombre} />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-medium text-gray-800">{l.nombre}</span>
                      <span className="block truncate text-xs text-gray-500">
                        {dominio(l.url)}
                        {l.embebible === false && <span className="ml-1 text-amber-600">· se abre en pestaña nueva</span>}
                      </span>
                    </span>
                  </button>
                  {puedeEditar ? (
                    <div className="flex flex-shrink-0 items-center">
                      <span className="hidden sm:flex">
                        <BotonIcono titulo="Subir" disabled={i === 0 || ocupado} onClick={() => mover(i, -1)}><ArrowUp size={18} /></BotonIcono>
                        <BotonIcono titulo="Bajar" disabled={i === links.length - 1 || ocupado} onClick={() => mover(i, 1)}><ArrowDown size={18} /></BotonIcono>
                      </span>
                      <span className="flex flex-col sm:hidden">
                        <button type="button" aria-label="Subir" title="Subir" disabled={i === 0 || ocupado} onClick={() => mover(i, -1)} className="w-8 h-5 flex items-center justify-center text-gray-500 disabled:opacity-30"><ArrowUp size={14} /></button>
                        <button type="button" aria-label="Bajar" title="Bajar" disabled={i === links.length - 1 || ocupado} onClick={() => mover(i, 1)} className="w-8 h-5 flex items-center justify-center text-gray-500 disabled:opacity-30"><ArrowDown size={14} /></button>
                      </span>
                      <BotonIcono titulo="Editar" onClick={() => { setABorrar(null); setEditando(l.id); }}><Pencil size={18} /></BotonIcono>
                      <BotonIcono titulo="Borrar" peligro onClick={() => { setEditando(null); setABorrar(l.id); }}><Trash2 size={18} /></BotonIcono>
                    </div>
                  ) : (
                    <BotonIcono titulo="Abrir en pestaña nueva" onClick={() => abrirAfuera(l.url)}><ExternalLink size={18} /></BotonIcono>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
